import { Box, Divider, HStack, VStack } from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CURRENCY_TYPE } from "../../../configs/constants/CurrencyType";
import { Salary } from "../../../models/Consultant";
import { useConsultant, useUpdateSalary } from "../../../networking/consultant.hooks";
import { useCollectiveAgreement } from "../../../networking/collective-agreement.hooks";
import { formatCurrencyString } from "../../../utils/string.utils";
import { FormLabel } from "../../Core/AppText/AppText";
import Button from "../../Core/Button/Button";
import AppInputNew from "@/components/Core/AppInput/AppInputNew";

interface Props {
	consultantId: string;
	showHistory?: boolean;
}

type EditableSalary = Pick<Salary, "amount" | "currency" | "taxPercent" | "taxTable">;

export const UpdateSalaryForm: React.FC<Props> = ({ consultantId, showHistory }) => {
	const { consultant } = useConsultant({ consultantId });

	const { t } = useTranslation();

	const { mutate, isLoading } = useUpdateSalary();

	const { latestSalary, salaryHistory } = useMemo(() => {
		const sortedSalariesByValidFrom = (consultant?.profile.employment?.salary || []).sort(
			(a, b) => new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime()
		);

		return {
			latestSalary: sortedSalariesByValidFrom[0],
			salaryHistory: sortedSalariesByValidFrom.slice(1),
		};
	}, [consultant]);

	const [value, setValue] = useState<EditableSalary>();

	useEffect(() => {
		if (!value && latestSalary) {
			setValue({
				amount: latestSalary.amount,
				taxPercent: latestSalary?.taxPercent,
				taxTable: latestSalary?.taxTable,
			});
		}
	}, [latestSalary]);

	function handleSubmit() {
		if (!consultant) return;

		mutate({
			consultantId: consultant.id,
			update: {
				amount: value?.amount,
				currency: CURRENCY_TYPE.SEK,
				note: latestSalary?.note || "",
				taxPercent: value?.taxPercent || undefined,
				taxTable: value?.taxTable || undefined,
			},
		});
	}

	function handleOnChange(value: EditableSalary) {
		setValue(value);
	}

	if (!consultant) return null;

	return (
		<>
			<SalaryForm
				onChange={handleOnChange}
				value={value}
				validFrom={latestSalary?.validFrom ? new Date(latestSalary.validFrom) : new Date()}
				consultantId={consultantId}
			/>
			<Box mt="4" alignSelf={"flex-start"}>
				<Button width="9.75rem" onClick={handleSubmit} loading={isLoading}>
					{t("consultant.salary.save")}
				</Button>
			</Box>
			{showHistory && latestSalary && (
				<VStack mt="7" w="full" alignItems={"flex-start"}>
					<Divider borderColor={"#ccc"} mb="4" />
					<FormLabel showKnowledgeBaseForTagOnHover="salary-history" margin="0">
						{t("consultant.salary.history")}
					</FormLabel>
					<Box
						display="flex"
						justifyContent={"space-between"}
						alignItems={"center"}
						w="full"
						p="2.5"
						bg="white"
						borderRadius="md"
						fontSize="sm"
					>
						<Box fontWeight={"medium"} color="gray.600">
							{formatCurrencyString(latestSalary?.amount || 0)} ({t("consultant.salary.current")})
						</Box>
						<Box textAlign={"right"} color="gray.500">
							<Box>
								{t("consultant.salary.validFrom", {
									date: dayjs(latestSalary?.validFrom || dayjs().startOf("month")).format(
										"YYYY-MM-DD"
									),
								})}
							</Box>
						</Box>
					</Box>
					{salaryHistory.map((salary, index) => (
						<Box
							display="flex"
							justifyContent={"space-between"}
							alignItems={"center"}
							key={index}
							w="full"
							p="2.5"
							bg="white"
							borderRadius="md"
							fontSize="sm"
						>
							<Box fontWeight={"medium"} color="gray.600">
								{formatCurrencyString(salary.amount || 0)}
							</Box>
							<Box textAlign={"right"} color="gray.500">
								<Box fontSize={"sm"}>
									{t("consultant.salary.validFrom", {
										date: dayjs(salary.validFrom).format("YYYY-MM-DD"),
									})}
								</Box>
							</Box>
						</Box>
					))}
				</VStack>
			)}
		</>
	);
};

export const SalaryForm: React.FC<{
	value?: EditableSalary;
	validFrom?: Date;
	onChange: (value: EditableSalary) => void;
	consultantId?: string;
}> = ({ value, validFrom, onChange, consultantId }) => {
	const { t } = useTranslation();
	const { consultant } = useConsultant({ consultantId }, !!consultantId);

	// Get holiday pay from collective agreement
	const collectiveAgreementId = consultant?.profile?.employment?.colAgreement?.id;
	const { collectiveAgreement } = useCollectiveAgreement(
		collectiveAgreementId,
		!!collectiveAgreementId
	);

	// Holiday pay always comes from collective agreement
	const holidayPayValue = collectiveAgreement?.vacationSalaryPercent || 0;

	const totPay = useMemo(() => {
		return (value?.amount || 0) * (holidayPayValue ? Number(holidayPayValue) / 100 + 1 : 1);
	}, [value, holidayPayValue]);

	function triggerOnChange(change: Partial<EditableSalary>) {
		onChange({
			...value,
			...change,
			currency: CURRENCY_TYPE.SEK,
		});
	}

	function handleInputDisabling(value: any) {
		if (value === undefined) {
			return false;
		}
		if (value === "") {
			return false;
		}

		if (value + "".trim()?.length > 0) {
			return true;
		}
	}

	return (
		<>
			<VStack>
				<AppInputNew
					type="number"
					// Named for forms and browser tests; the value is controlled above
					name="salary.amount"
					value={value?.amount}
					onChangeNumber={(amount) => triggerOnChange({ amount })}
					label="HOURLY_PAY"
					mandatory={true}
					description={t("TOT_HOURLY_PAY", {
						tot: formatCurrencyString(totPay),
						date: dayjs(validFrom).format("YYYY-MM-DD"),
						holidayPay: (holidayPayValue || 0).toString().replace(".", ","),
					})}
					showKnowledgeBaseForTagOnHover="hourly-pay"
				/>
				<div className="grid grid-cols-2 gap-4 gap-y-2 w-full items-start">
					{/* <AppInputNew
						type="number"
						value={holidayPayValue}
						onChangeNumber={() => {}} // Read-only - always from collective agreement
						label={t("HOLIDAY_PAY")}
						disabled={true}
						wrapperClassName="bg-gray-100"
						mandatory={false}
						description={
							collectiveAgreement
								? `Styrs av kollektivavtal ${collectiveAgreement.name}`
								: "Ingen kollektivavtal tilldelat"
						}
						showKnowledgeBaseForTagOnHover="holiday-pay"
					/> */}
					<AppInputNew
						type="number"
						disabled={handleInputDisabling(value?.taxTable)}
						value={value?.taxPercent ?? (!value?.taxTable ? 30 : undefined)}
						onChangeNumber={(taxPercent) => triggerOnChange({ taxPercent })}
						label="TAX_FIELD"
						mandatory={false}
						showKnowledgeBaseForTagOnHover="tax-percent"
						prefix="%"
					/>
					<AppInputNew
						type="number"
						disabled={handleInputDisabling(value?.taxPercent)}
						value={value?.taxTable}
						onChangeNumber={(taxTable) => triggerOnChange({ taxTable })}
						label="TABLE"
						mandatory={false}
						showKnowledgeBaseForTagOnHover="tax-table"
					/>
				</div>
			</VStack>
		</>
	);
};

interface SalaryFormControllerProps {
	control: Control<any, any>;
	name: string;
	validFrom?: Date;
	// errors?: any;
}

export const SalaryFormController: React.FC<SalaryFormControllerProps> = ({
	control,
	name,
	validFrom,
}) => {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field: { ref, ...fieldWithoutRef } }) => (
				<SalaryForm validFrom={validFrom} {...fieldWithoutRef} />
			)}
		/>
	);
};
